import React from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { Action, Location } from 'history';
import { useSsmConfigBridge } from '@utilities/hooks/useSsmConfigBridge/useSsmConfigBridge';
import { useMessageController } from '@utilities/hooks/useMessageController/useMessageController';
import { FAX_MANAGEMENT_URL } from '@utilities/constants';
import { useHomeNavigationContext } from '@components/HeaderSection/NavigationOptions/HomeNavigationContext/HomeNavigationContext';
import { ROUTE_CONSTANTS } from '@utilities/RouteConstants';

const useStyles = makeStyles({
	container: {
		position: 'fixed',
		top: 68,
		left: 0,
		right: 0,
		bottom: 0,
	},
	iframe: {
		border: 'none',
		display: 'block',
		height: '100%',
		width: '100%',
	},
});

// Key for sending naigation through SSM
const NAVIGATE = 'NAVIGATE';
// Asks the iframe to persist unsaved PDF edits before it is torn down
const FLUSH_PENDING_SAVES = 'FLUSH_PENDING_SAVES';
const FLUSH_PENDING_SAVES_TIMEOUT_MS = 10000;

export const FaxManagementPage: React.FC = () => {
	const classes = useStyles();
	const history = useHistory();
	const location = useLocation();
	const shellRoute = location.pathname + location.search;

	const iframeRef = React.useRef<HTMLIFrameElement>(null);
	const iframeRouteRef = React.useRef(shellRoute);
	const [initialSrc] = React.useState(
		() => FAX_MANAGEMENT_URL + shellRoute.replace(/^\/faxmanagement/, ''),
	);

	const { setHomeRoute } = useHomeNavigationContext();

	React.useEffect(() => {
		setHomeRoute(ROUTE_CONSTANTS.CASE_LIST);
	}, [setHomeRoute]);

	const controller = useMessageController(iframeRef, {
		targetOrigin: new URL(window.config.UI_SHELL_URL).origin,
		onRouteChange: (route) => {
			iframeRouteRef.current = route;
			if (history.location.pathname + history.location.search !== route) {
				history.replace(route);
			}
		},
	});

	useSsmConfigBridge(controller);

	const controllerRef = React.useRef(controller);
	React.useEffect(() => {
		controllerRef.current = controller;
	});

	// Leaving Fax Management unmounts the iframe before its async saves can finish, so flush first.
	React.useEffect(() => {
		let isFlushing = false;
		let pending: { location: Location; action: Action } | null = null;

		const unblock = history.block((nextLocation, action) => {
			const isFaxManagementRoute =
				nextLocation.pathname === ROUTE_CONSTANTS.FAX_MANAGEMENT ||
				nextLocation.pathname.startsWith(`${ROUTE_CONSTANTS.FAX_MANAGEMENT}/`);
			// Browser back/forward cannot be safely replayed; only intercept link-driven navigation.
			if (isFaxManagementRoute || action === 'POP' || !controllerRef.current.isConnected) {
				pending = null;
				return;
			}

			pending = { location: nextLocation, action };
			if (isFlushing) return false;
			isFlushing = true;

			void controllerRef.current
				.request(FLUSH_PENDING_SAVES, {}, FLUSH_PENDING_SAVES_TIMEOUT_MS)
				.catch(() => undefined)
				.then(() => {
					isFlushing = false;
					const target = pending;
					pending = null;
					if (!target) return;
					unblock();
					if (target.action === 'REPLACE') history.replace(target.location);
					else history.push(target.location);
				});
			return false;
		});

		return unblock;
	}, [history]);

	// Sync iframe when the shell route changes externally (e.g. nav link click)
	React.useEffect(() => {
		if (iframeRouteRef.current === shellRoute) return;
		controller.send(NAVIGATE, { path: shellRoute });
		iframeRouteRef.current = shellRoute;
	}, [shellRoute, controller]);

	return (
		<Box className={classes.container}>
			<iframe
				ref={iframeRef}
				title="Fax Management Page"
				src={initialSrc}
				allow="local-network-access"
				className={classes.iframe}
			/>
		</Box>
	);
};
